import { yupResolver } from '@hookform/resolvers/yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputBaseComponentProps,
  InputProps,
  Link,
  Stack,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { QuestionnaireResponseItem } from 'fhir/r4b';
import _ from 'lodash';
import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import Markdown from 'react-markdown';
import { useBeforeUnload } from 'react-router-dom';
import { formatZipcodeForDisplay, stripMarkdownLink } from 'utils/lib/helpers/helpers';
import { buildCalculatedAnswers } from 'utils/lib/helpers/paperwork/calculated-expressions';
import { pickFirstValueFromAnswerItem } from 'utils/lib/helpers/paperwork/paperwork';
import { makeValidationSchema, SIGNATURE_FIELDS } from 'utils/lib/helpers/paperwork/validation';
import { qrSentManually } from 'utils/lib/helpers/practice-managed-questionnaires';
import {
  IntakeQuestionnaireItem,
  QuestionnaireFormFields,
  QuestionnaireItemGroupType,
} from 'utils/lib/types/data/paperwork/paperwork.types';
import { zipRegex } from 'utils/lib/validation/regex';
import { AnyObjectSchema } from 'yup';
import { InputMask } from '../InputMask';
import { usePaperworkContext } from './context';
import AIInterview from './form-components/AIInterview';
import { BoldPurpleInputLabel } from './form-components/BoldPurpleInputLabel';
import { CalculatedField } from './form-components/CalculatedField';
import { ControlButtons } from './form-components/ControlButtons';
import { CardErrorDialog } from './form-components/credit-card/CardErrorDialog';
import { CreditCardVerification } from './form-components/credit-card/CreditCardVerification';
import { DateInput } from './form-components/DateInput';
import { DecimalInput } from './form-components/DecimalInput';
import { DescriptionRenderer } from './form-components/DescriptionRenderer';
import { FieldHelperText } from './form-components/FieldHelperText';
import FileInput from './form-components/FileInput';
import { AttachmentType } from './form-components/FileInput';
import { FreeMultiSelectInput } from './form-components/FreeMultiSelectInput';
import GroupContainer from './form-components/group/GroupContainer';
import MultiAnswerHeader from './form-components/group/MultiAnswerHeader';
import { LightToolTip } from './form-components/LightToolTip';
import { LinkRenderer } from './form-components/LinkRenderer';
import { PaperworkAiSuggestionRow } from './form-components/PaperworkAiSuggestionRow';
import { hasPaperworkAiSuggestion } from './form-components/PaperworkAiSuggestionRow';
import { PharmacyCollection } from './form-components/PharmacyCollection';
import { RadioInput } from './form-components/RadioInput';
import { RadioListInput } from './form-components/RadioListInput';
import { useAutoFillValues } from './hooks/useAutofill';
import { useCalculationInput } from './hooks/useCalculationInput';
import { useCreditCardSave } from './hooks/useCreditCardSave';
import { useDisplayFilteredOptions, useFilterAnswersOptions } from './hooks/useFilterAnswersOptions';
import { getPaperworkFieldId, useFieldError, usePaperworkFormHelpers, useQRState } from './hooks/useFormHelpers';
import { StyledQuestionnaireItem, useStyledItems } from './hooks/useStyleItems';
import { PaperworkThemeProvider, usePaperworkOtherColors } from './theme';
import { ControlButtonsProps } from './types';
import { getInputTypeForItem, getUCInputType } from './utils';

interface PagedQuestionnaireOptions {
  bottomComponent?: ReactElement;
  hideControls?: boolean;
  controlButtons?: ControlButtonsProps;
}

interface PagedQuestionnaireInput {
  items: IntakeQuestionnaireItem[];
  pageId: string;
  pageItem?: IntakeQuestionnaireItem;
  pageSubtitle?: string;
  defaultValues?: QuestionnaireFormFields;
  options?: PagedQuestionnaireOptions;
  isSaving?: boolean;
  onSubmit: (data: QuestionnaireFormFields, extras: PagedQuestionnaireSubmitExtras) => void;
  saveProgress: (data: QuestionnaireFormFields) => void;
  skipValidation: boolean;
}

export interface PagedQuestionnaireSubmitExtras {
  /**
   * The values of the form's calculated items, worked out in the browser from the saved response and the values
   * just entered. They cover every page (including hidden ones that are never saved themselves) and only the values
   * that need to change. The host sends them along with the page it saves, as `calculatedAnswers`.
   */
  calculatedAnswers: QuestionnaireResponseItem[];
}

type FormInputProps = {
  format?: string;
  helperText?: string;
  showHelperTextIcon?: boolean;
  inputBaseProps?: InputBaseComponentProps;
} & InputProps;

interface ItemInputProps {
  item: IntakeQuestionnaireItem;
  inputProps?: FormInputProps;
  sx?: SxProps<Theme>;
}

interface StyledItemInputProps extends ItemInputProps {
  item: StyledQuestionnaireItem;
}

const DEFAULT_INPUT_BASE_PROPS: InputBaseComponentProps = {
  width: '100%',
};

const makeFormInputPropsForItem = (item: StyledQuestionnaireItem): FormInputProps => {
  const { mask } = item;

  const inputProps = {
    format: undefined,
    infoText: undefined,
    helperText: undefined,
    showHelperTextIcon: false,
    inputBaseProps: {
      ...DEFAULT_INPUT_BASE_PROPS,
      mask,
    },
  };

  // (`input props for item ${item.linkId}`, inputProps);
  return inputProps;
};

const makeFormErrorMessage = (items: IntakeQuestionnaireItem[], errors: any): string | undefined => {
  const visibleLinkIds = new Set(items.map((i) => i.linkId));
  const errorKeys = Object.keys(errors).filter((k) => visibleLinkIds.has(k));
  let numErrors = errorKeys.length;
  if (numErrors === 0) {
    return undefined;
  }

  // Check if any error is for a group with list-with-form type
  const hasListWithFormError = items.some((item) => {
    return errorKeys.includes(item.linkId) && item.type === 'group' && item.groupType === 'list-with-form';
  });

  // For list-with-form groups, always use the generic singular message
  if (hasListWithFormError) {
    return 'Please fix the error in the field above to proceed';
  }

  const errorItems = items
    .filter((i) => errorKeys.includes(i.linkId) && (i.text !== undefined || i.type === 'group'))
    .flatMap((i) => {
      if (i.type === 'group' && i.dataType !== 'DOB') {
        const items = ((errors[i.linkId] as any)?.item ?? []) as any[];
        if (!Array.isArray(items)) {
          // If items is not an array, treat it as a single group error
          return `"${stripMarkdownLink(i.text ?? i.linkId)}"`;
        }

        const internalErrors: IntakeQuestionnaireItem[] = [];
        items.forEach((e, idx) => {
          if (e != null) {
            const errorItem = (i.item ?? []).filter(
              (i) => i.type !== 'display' && !i.readOnly && !i.calculatedExpression
            )[idx];
            if (errorItem) {
              internalErrors.push(errorItem);
            }
          }
        });
        numErrors += internalErrors.length - 1;
        return internalErrors.map((nestedItem) => {
          return `"${stripMarkdownLink(nestedItem.text ?? '')}"`;
        });
      }
      return `"${stripMarkdownLink(i.text ?? '')}"`;
    });

  if (numErrors === errorItems.length) {
    if (numErrors > 1) {
      return `Please fix the errors in the following fields to proceed: ${errorItems.map((ei) => ei)}`;
    } else {
      return `Please fix the error in the ${errorItems[0]} field to proceed`;
    }
  } else if (numErrors === 1) {
    return 'Please fix the error in the field above to proceed';
  } else {
    return `Please fix the error in the ${numErrors} fields above to proceed`;
  }
};

const PagedQuestionnaire: FC<PagedQuestionnaireInput> = ({
  items,
  pageId,
  pageItem,
  pageSubtitle,
  defaultValues,
  options = {},
  isSaving,
  onSubmit,
  saveProgress,
  skipValidation,
}) => {
  const { paperwork, allItems, questionnaireResponse } = usePaperworkContext();
  const [cache, setCache] = useState({
    pageId,
    items,
    defaultValues,
  });
  const validationSchema = makeValidationSchema(items, pageId, {
    values: paperwork,
    items: allItems,
    questionnaireResponse,
  }) as AnyObjectSchema;

  const methods = useForm<QuestionnaireFormFields>({
    mode: 'onSubmit', // onBlur doesn't seem to work but we use onBlur of FormControl in NestedInput to implement the desired behavior
    reValidateMode: 'onChange',
    context: paperwork,
    defaultValues,
    shouldFocusError: true,
    resolver: skipValidation ? undefined : yupResolver(validationSchema, { abortEarly: false }),
  });
  const { reset } = methods;

  useEffect(() => {
    if (
      items &&
      (cache.pageId !== pageId || !_.isEqual(cache.items, items) || !_.isEqual(cache.defaultValues, defaultValues))
    ) {
      setCache({ pageId, items, defaultValues });
      reset((formValues) => ({
        ...formValues,
        ...(defaultValues ?? {}),
      }));
    }
  }, [cache, defaultValues, items, reset, pageId]);
  return (
    <PaperworkThemeProvider>
      <FormProvider {...methods}>
        {pageItem && pageSubtitle ? <PaperworkPageTitle pageItem={pageItem} pageSubtitle={pageSubtitle} /> : null}
        <PaperworkFormRoot
          pageItem={pageItem}
          items={items}
          onSubmit={onSubmit}
          saveProgress={saveProgress}
          options={options}
          parentIsSaving={isSaving}
        />
      </FormProvider>
    </PaperworkThemeProvider>
  );
};

interface PaperworkPageTitleProps {
  pageItem: IntakeQuestionnaireItem;
  pageSubtitle: string;
}

const PaperworkPageTitle: FC<PaperworkPageTitleProps> = ({ pageItem, pageSubtitle }) => {
  const theme = useTheme();
  const [styledPageItem] = useStyledItems({ formItems: [pageItem] });
  return (
    <Stack style={{ marginBottom: '16px' }}>
      <Typography
        sx={{
          width: { xs: '100%', md: '100%' },
        }}
        variant="h2"
        color="primary.main"
        data-testid="flow-page-title"
      >
        {styledPageItem?.text}
      </Typography>
      {pageSubtitle && (
        <Typography variant="body2" color={theme.palette.secondary.main} fontSize={'18px'}>
          {pageSubtitle}
        </Typography>
      )}
    </Stack>
  );
};

interface PaperworkRootInput {
  pageItem?: IntakeQuestionnaireItem;
  items: IntakeQuestionnaireItem[];
  onSubmit: (data: QuestionnaireFormFields, extras: PagedQuestionnaireSubmitExtras) => void;
  saveProgress: (data: QuestionnaireFormFields) => void;
  options?: PagedQuestionnaireOptions;
  parentIsSaving?: boolean;
}
const PaperworkFormRoot: FC<PaperworkRootInput> = ({
  pageItem,
  items,
  onSubmit,
  saveProgress,
  options = {},
  parentIsSaving = false,
}) => {
  const [isSavingProgress, setIsSavingProgress] = useState(false);

  const { questionnaireResponse, saveButtonDisabled, continueLabel } = usePaperworkContext();

  const { handleSubmit, formState } = useFormContext();

  const { isSavingCard, handleCardSave } = useCreditCardSave();

  const theme = useTheme();

  const { isSubmitting, isLoading, errors } = formState;

  const visibleItems = useStyledItems({ formItems: items });
  const errorMessage = makeFormErrorMessage(visibleItems, errors);
  const { formValues } = useQRState();

  // Works out the form's calculated items, hidden pages included, from the saved response overlaid with the values
  // just entered. The values are sent with this page's save, because a hidden page is never saved by itself.
  const calculationInput = useCalculationInput();
  const makeCalculatedAnswers = useCallback(
    (data: QuestionnaireFormFields): QuestionnaireResponseItem[] => buildCalculatedAnswers(calculationInput(data)),
    [calculationInput]
  );

  // Only run credit-card auto-save when the current page actually contains a Credit Card input.
  // The credit card store is module-scoped and survives across navigations, so without this guard
  // a stale `isCreditCardRequired: true` from a previously-visited credit card page (where the
  // user didn't fill the card) would silently block submit on unrelated pages like Patient
  // Information.
  const hasCreditCardField = useMemo(() => {
    const containsCreditCardInput = (xs: IntakeQuestionnaireItem[]): boolean =>
      xs.some((item) => getInputTypeForItem(item) === 'Credit Card' || containsCreditCardInput(item.item ?? []));
    return containsCreditCardInput(items);
  }, [items]);

  const submitHandler = useCallback(
    async ({ skipValidation = false }: { skipValidation?: boolean } = {}) => {
      console.log('skipValidation', skipValidation);
      if (hasCreditCardField) {
        console.log('the page has a credit card field');
        const { shouldContinue } = await handleCardSave({ skipValidation });

        if (!shouldContinue) {
          console.log(`cannot continue with card, skipping submit`);
          return;
        }
      }

      setIsSavingProgress(true);
      await handleSubmit((data) => onSubmit(data, { calculatedAnswers: makeCalculatedAnswers(data) }))();
      setIsSavingProgress(false);
    },
    [handleSubmit, onSubmit, handleCardSave, hasCreditCardField, makeCalculatedAnswers]
  );

  const { bottomComponent, hideControls, controlButtons } = options;
  const swizzledCtrlButtons = useMemo(() => {
    const baseStuff = controlButtons ?? {};

    const useContinueLabel =
      questionnaireResponse?.questionnaire?.includes('intake-paperwork-inperson') ||
      qrSentManually(questionnaireResponse);

    return {
      ...baseStuff,
      submitDisabled: baseStuff.loading || isLoading || saveButtonDisabled,
      // only use the continue label with inperson paperwork
      submitLabel: useContinueLabel ? continueLabel : undefined,
      onSubmit: submitHandler,
    };
  }, [continueLabel, controlButtons, isLoading, questionnaireResponse, saveButtonDisabled, submitHandler]);

  useBeforeUnload(() => {
    saveProgress(formValues);
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submitHandler();
      }}
    >
      <Grid container spacing={1}>
        <RenderItems items={items} pageItem={pageItem} />
      </Grid>
      <div id="page-form-inner-form" />
      {bottomComponent}
      {errorMessage && (
        <FormHelperText
          id={'form-error-helper-text'}
          sx={{ textAlign: 'right', color: theme.palette.error.main, gap: 0, mt: 1 }}
        >
          {errorMessage}
        </FormHelperText>
      )}
      {!hideControls && (
        <ControlButtons
          {...swizzledCtrlButtons}
          loading={isSavingProgress || isSubmitting || parentIsSaving || isSavingCard}
        />
      )}
      <CardErrorDialog onContinueAnyway={() => void submitHandler({ skipValidation: true })} />
    </form>
  );
};

export interface RenderItemsProps {
  items: IntakeQuestionnaireItem[];
  pageItem?: IntakeQuestionnaireItem;
  parentItem?: IntakeQuestionnaireItem;
  fieldId?: string;
}

const RenderItems: FC<RenderItemsProps> = (props: RenderItemsProps) => {
  const { items, pageItem, parentItem, fieldId } = props;
  const styledItems = useStyledItems({ formItems: items });
  useAutoFillValues({ questionnaireItems: items, fieldId, parentItem });

  return (
    <>
      {styledItems.map((item, idx) => {
        if (item.type === 'display') {
          return <FormDisplayField item={item} key={`FDF-${fieldId ?? item.linkId}-${idx}`} />;
        } else if (item.calculatedExpression) {
          return <CalculatedField item={item} key={`CF-${fieldId ?? item.linkId}-${idx}`} />;
        } else if (
          item.type === 'group' &&
          item.dataType !== 'DOB' &&
          item.groupType !== QuestionnaireItemGroupType.PharmacyCollection
        ) {
          return (
            <GroupContainer
              item={item}
              key={`${JSON.stringify(item)}-${idx}`}
              fieldId={fieldId}
              parentItem={parentItem}
              pageItem={pageItem}
              RenderItems={RenderItems}
            />
          );
        } else {
          return (
            <NestedInput
              key={`NI-${JSON.stringify(item)}-${idx}`}
              item={item}
              inputProps={makeFormInputPropsForItem(item)}
              pageItem={pageItem}
              parentItem={props.parentItem}
              inheritedFieldId={fieldId}
            />
          );
        }
      })}
    </>
  );
};

// this probably has a more specific type but not sure what it is right now
const makeStyles = (): any => {
  const signatureFont = 'Dancing Script, Tangerine, Bradley Hand, Brush Script MT, sans-serif';
  return {
    signatureStyles: {
      input: {
        fontFamily: signatureFont,
        fontSize: '20px',
        fontWeight: 500,
      },
      'input::placeholder': {
        fontFamily: signatureFont,
      },
    },
  };
};

interface NestedInputProps extends StyledItemInputProps {
  pageItem?: IntakeQuestionnaireItem;
  parentItem?: IntakeQuestionnaireItem;
  inheritedFieldId?: string;
}

const NestedInput: FC<NestedInputProps> = (props) => {
  const { item, inputProps, sx = {}, pageItem, parentItem, inheritedFieldId } = props;
  const { helperText, showHelperTextIcon } = inputProps || {};
  const { formValues } = useQRState();
  const dependency = item.requireWhen ? formValues[item.requireWhen.question] : undefined;
  const otherColors = usePaperworkOtherColors();
  const { trigger } = useFormContext();
  const [isFocused, setIsFocused] = useState(false);
  const { appointment } = usePaperworkContext();
  const hasAiSuggestion = hasPaperworkAiSuggestion(item.linkId);

  // fieldId returns the path to the scalar value (the thing that the inputs manipulate directly)
  // call site 2: ignores result when no parent item
  const fieldId = getPaperworkFieldId({ item, parentItem, parentFieldId: inheritedFieldId });
  const { hasError, errorMessage } = useFieldError(parentItem ? fieldId : item.linkId);

  useEffect(() => {
    if (hasError && dependency) {
      void trigger(fieldId);
    }
  }, [hasError, dependency, trigger, item.linkId, parentItem, fieldId]);

  return (
    <Grid item xs={12} md={item.width} sx={{ maxWidth: '100%' }}>
      <Controller
        name={fieldId}
        render={(renderProps) => (
          <FormControl
            variant="standard"
            required={item.dataType !== 'Payment Validation' && item.isRequired}
            error={hasError}
            fullWidth={item.isFullWidth}
            hiddenLabel={true}
            disabled={item.displayStrategy === 'protected'}
            onBlur={() => {
              if (getInputTypeForItem(item) === 'Text') {
                void trigger(fieldId);
              }
              setIsFocused(false);
            }}
            onChange={() => {
              if (hasError) {
                void trigger(fieldId);
              }
            }}
            onFocus={() => setIsFocused(true)}
            margin={'dense'}
            sx={{
              width: '100%',
              ...sx,
              '& .MuiInputBase-root': {
                marginTop: '0px',
              },
            }}
          >
            <BoldPurpleInputLabel
              id={`${item.linkId}-label`}
              htmlFor={`${item.linkId}`}
              sx={(theme: Theme) => ({
                ...(item.hideControlLabel ? { display: 'none' } : { whiteSpace: 'pre-wrap', position: 'unset' }),
                color: isFocused ? theme.palette.primary.dark : theme.palette.primary.main,
              })}
            >
              {item.infoText ? (
                <Tooltip enterTouchDelay={0} title={item.infoText} placement="top" arrow>
                  <Box>
                    {item.text}
                    <IconButton>
                      <InfoOutlinedIcon sx={{ fontSize: '18px', color: 'secondary.main' }} />
                    </IconButton>
                  </Box>
                </Tooltip>
              ) : (
                item.text
              )}
            </BoldPurpleInputLabel>
            <FormInputField
              renderProps={renderProps}
              itemProps={props}
              pageItem={pageItem}
              parentItem={parentItem}
              fieldId={fieldId}
            />
            {item.secondaryInfoText ? (
              <LightToolTip
                title={item.secondaryInfoText}
                placement="top"
                enterTouchDelay={0}
                backgroundColor={otherColors.toolTipGrey}
                color={otherColors.black}
              >
                <Box
                  sx={{
                    color: otherColors.scheduleBorder,
                    width: 'fit-content',
                    display: 'flex',
                    marginTop: 0.5,
                    cursor: 'default',
                  }}
                >
                  <InfoOutlinedIcon style={{ height: '16px', width: '16px' }} />
                  <Typography sx={{ fontSize: '14px', marginLeft: 0.5 }}>Why do we ask this?</Typography>
                </Box>
              </LightToolTip>
            ) : null}
            <FieldHelperText
              name={fieldId}
              hasError={hasError}
              helperText={helperText}
              showHelperTextIcon={showHelperTextIcon}
              errorMessage={errorMessage}
            />
            {hasAiSuggestion && (
              <PaperworkAiSuggestionRow linkId={item.linkId} fieldId={fieldId} appointmentId={appointment?.id} />
            )}
          </FormControl>
        )}
      />
    </Grid>
  );
};

interface GetFormInputFieldProps {
  itemProps: StyledItemInputProps;
  renderProps: any; // do better
  fieldId: string;
  pageItem?: IntakeQuestionnaireItem;
  parentItem?: IntakeQuestionnaireItem;
}

const FormInputField: FC<GetFormInputFieldProps> = ({
  itemProps,
  renderProps,
  fieldId,
  pageItem,
  parentItem,
}): ReactElement => {
  const { item, inputProps } = itemProps;
  const { inputBaseProps, inputMode } = inputProps || { disableUnderline: true };
  const {
    field: { value, onChange, ref },
    formState: { defaultValues },
  } = renderProps;
  const inputType = getInputTypeForItem(item);
  const otherColors = usePaperworkOtherColors();
  const theme = useTheme();
  const myInputComponent = inputBaseProps?.mask ? (InputMask as any) : 'input';

  const { answerLoadingOptions } = item;
  const hasDynamicAnswerOptions = answerLoadingOptions?.strategy === 'dynamic';

  const styles = useMemo(() => {
    return makeStyles();
  }, []);

  const inputSX = useMemo(() => {
    return SIGNATURE_FIELDS.includes(item.linkId)
      ? { ...styles.inputStyles, ...styles.signatureStyles }
      : styles.inputStyles;
  }, [item.linkId, styles.inputStyles, styles.signatureStyles]);

  const {
    onChange: smartOnChange,
    inputRef,
    value: unwrappedValue,
  } = usePaperworkFormHelpers({ item, renderValue: value, renderOnChange: onChange, fieldId });

  const error = useFieldError(fieldId);
  const displayFilteredOptions = useDisplayFilteredOptions(item.answerDisplayFilters, item.answerOption ?? []);
  const answerOptions = useFilterAnswersOptions(displayFilteredOptions);
  const colorForButton = unwrappedValue ? theme.palette.destructive.main : theme.palette.primary.main;
  let attachmentType: AttachmentType = 'image';
  if (item.dataType === 'PDF') {
    attachmentType = 'pdf';
  }

  return (() => {
    switch (inputType) {
      case 'Text':
        return (
          <TextField
            id={fieldId}
            value={item.dataType === 'ZIP' ? formatZipcodeForDisplay(unwrappedValue) : unwrappedValue}
            type={getUCInputType(item?.dataType)}
            aria-labelledby={`${item.linkId}-label`}
            aria-describedby={`${item.linkId}-helper-text`}
            inputProps={{
              ...inputBaseProps,
              maxLength: item.maxLength,
              inputMode:
                inputMode ??
                (item.type === 'decimal'
                  ? 'decimal'
                  : item.type === 'integer' || item.dataType === 'ZIP'
                  ? 'numeric'
                  : 'text'),
              ...(item.dataType === 'ZIP' && { pattern: zipRegex, maxLength: 10 }),
            }}
            placeholder={item.placeholder}
            required={item.required}
            onChange={smartOnChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              multiline: item.multiline,
              minRows: item.minRows,
              inputComponent: myInputComponent,
              disabled: item.displayStrategy !== 'enabled',
              ref: parentItem ? ref : inputRef,
              error: error.hasError,
            }}
            // this also overrides explicitly passed in sx in inputProps...
            sx={{
              ...inputSX,
              '.MuiOutlinedInput-root': {
                borderRadius: '8px',
                height: 'auto',
                width: '100%',
                padding: item.minRows ? '12px 16px' : '2px 2px',
                '&.Mui-focused': {
                  boxShadow: '0 -0.5px 0px 3px rgba(77, 21, 183, 0.25)',
                  '& fieldset': {
                    borderWidth: '1px',
                  },
                },
              },
            }}
            size="small"
          />
        );
      case 'Free Select':
      case 'Select':
        if (!item.answerOption && !hasDynamicAnswerOptions) {
          throw new Error('No selectOptions given in select 2');
        }
        return (
          <FreeMultiSelectInput
            name={fieldId}
            disabled={item.displayStrategy !== 'enabled'}
            value={unwrappedValue}
            multiple={item.acceptsMultipleAnswers}
            freeSolo={item.type === 'open-choice'}
            defaultValue={(defaultValues && pickFirstValueFromAnswerItem(defaultValues[item.linkId])) ?? null}
            required={item.required}
            options={answerOptions}
            dynamicAnswerOptions={answerLoadingOptions}
            answerValueSet={item.answerValueSet}
            inputRef={ref}
            onChange={smartOnChange}
          />
        );
      case 'Button':
        return (
          <Button
            variant="outlined"
            type="button"
            onClick={
              item.type !== 'boolean'
                ? smartOnChange
                : () => {
                    smartOnChange(!unwrappedValue);
                  }
            }
            sx={{
              color: colorForButton,
              borderColor: colorForButton,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                borderColor: colorForButton,
              },
            }}
          >
            {item.text}
          </Button>
        );
      case 'Checkbox':
        return (
          <FormControlLabel
            label={<Markdown components={{ p: DescriptionRenderer, a: LinkRenderer }}>{item.text}</Markdown>}
            sx={{ pt: item.hideControlLabel ? 0 : 1, alignItems: 'flex-start', margin: '0px' }}
            control={
              <Checkbox
                checked={unwrappedValue}
                color="primary"
                style={{ borderRadius: '4px' }}
                aria-label={`${item.linkId}-label`}
                sx={{
                  alignSelf: 'flex-start',
                  width: '18px',
                  height: '18px',
                  display: 'inline-flex',
                  marginTop: '0px',
                  marginRight: '10px',
                  '&.MuiCheckbox-root': {
                    borderRadius: '4px',
                  },
                  '&.Mui-checked': {
                    color: otherColors.purple,
                    borderRadius: '4px',
                  },
                }}
                onChange={() => smartOnChange(!unwrappedValue)}
                required={item.required}
              />
            }
          />
        );
      case 'Radio':
        return (
          <RadioInput
            name={fieldId}
            value={unwrappedValue}
            required={item.required}
            options={answerOptions}
            onChange={smartOnChange}
          />
        );
      case 'Radio List':
        return (
          <RadioListInput
            name={fieldId}
            value={unwrappedValue}
            required={item.required}
            options={answerOptions}
            onChange={smartOnChange}
          />
        );
      case 'Date':
        return (
          <DateInput
            value={unwrappedValue}
            item={item}
            fieldId={parentItem ? fieldId : item.linkId}
            onChange={smartOnChange}
          />
        );
      case 'Decimal':
        return (
          <DecimalInput
            item={item}
            value={unwrappedValue}
            fieldId={fieldId}
            onChange={smartOnChange}
            inputRef={parentItem ? ref : inputRef}
            error={error.hasError}
            disabled={item.displayStrategy !== 'enabled'}
          />
        );
      case 'Attachment':
        return (
          <FileInput
            fileName={item.linkId}
            fieldName={fieldId}
            attachmentType={attachmentType}
            value={unwrappedValue}
            onChange={smartOnChange}
            description={item.attachmentText ?? ''}
            usePaperworkContext={usePaperworkContext}
          />
        );
      case 'Group':
        // will this ever be reached??
        if (item.groupType == QuestionnaireItemGroupType.ListWithForm) {
          return (
            <>
              <MultiAnswerHeader item={item} key={`${fieldId}.group-header`} />
              <RenderItems
                pageItem={pageItem}
                parentItem={item}
                items={item.item ?? []}
                fieldId={fieldId}
                key={`${fieldId}.group-render`}
              />
            </>
          );
        } else if (item.groupType == QuestionnaireItemGroupType.PharmacyCollection) {
          return <PharmacyCollection onChange={smartOnChange} />;
        } else {
          return <RenderItems pageItem={pageItem} parentItem={item} items={item.item ?? []} fieldId={fieldId} />;
        }
      case 'Credit Card':
        return (
          <CreditCardVerification
            fieldId={fieldId}
            onChange={smartOnChange}
            required={item.isRequired}
            value={unwrappedValue}
          />
        );
      case 'Medical History':
        return <AIInterview value={unwrappedValue} onChange={smartOnChange} />;
      case 'Link':
        return (
          <Link
            component="button"
            type="button"
            onClick={() => smartOnChange(!unwrappedValue)}
            aria-label={`${item.linkId}-toggle`}
            underline="hover"
            sx={{
              pt: item.hideControlLabel ? 0 : 1,
              textAlign: 'left',
              display: 'inline',
              cursor: 'pointer',
              color: otherColors.purple,
              fontWeight: 500,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {item.text}
          </Link>
        );
      default:
        return <></>;
    }
  })();
};

interface FormDisplayFieldProps {
  item: StyledQuestionnaireItem;
}

const FormDisplayField: FC<FormDisplayFieldProps> = ({ item }): ReactElement => {
  const displayType = getInputTypeForItem(item);
  const otherColors = usePaperworkOtherColors();

  const element = (() => {
    switch (displayType) {
      case 'Call Out':
        return (
          <Card sx={{ p: 2, backgroundColor: otherColors.coachingVisit, borderRadius: 2 }} elevation={0}>
            <Typography color="primary.main">{item.text}</Typography>
          </Card>
        );
      case 'Header 4':
        return (
          <Box mb={1} key={`form-display-H4-${item.linkId}-${item.text}`}>
            <Typography variant="h4" color="primary">
              {item.text}
            </Typography>
          </Box>
        );
      case 'Header 3':
        return (
          <Box mb={1} key={`form-display-H3-${item.linkId}-${item.text}`}>
            <Typography variant="h3" color="primary">
              {item.text}
            </Typography>
          </Box>
        );
      case 'Description':
        return (
          <Typography
            variant="body1"
            key={`form-display-body1-${item.linkId}-${item.text}`}
            sx={{ paddingBottom: '10px' }}
          >
            {item.text}
          </Typography>
        );
      default:
        return <></>;
    }
  })();
  return (
    <Grid item xs={12} md={item.width} key={`form-display-field-${item.linkId}`} paddingLeft="0px">
      {element}
    </Grid>
  );
};

export default PagedQuestionnaire;
